import type { AgentConfig } from "./index";

// Minimal direct-browser client for the deployed Foundry hosted agent's Responses-protocol
// endpoint.

export interface McpUiResource {
  uri: string;
  mimeType: string;
  text?: string;
}

export type ChatEvent =
  | { kind: "text"; text: string }
  | { kind: "tool-call"; name: string; args?: string }
  | { kind: "resource"; resource: McpUiResource; toolResult: RawCallToolResult }
  // A toolbox tool (e.g. Work IQ) needs the user to grant access first. The turn stops there; once
  // the user has opened consentLink and signed in, they send their message again.
  | { kind: "consent"; serverLabel: string; consentLink: string }
  // The agent's start_over on a Viewer conversation: nothing is cleared server-side, the host starts a
  // fresh chat itself (see StartOverResponse.NewChat).
  | { kind: "new-chat" };

/** A case file attached to the next message. n is its position, which the user and the agent call it by (#1, #2). */
export interface FileChip {
  n: number;
  fileReference: string;
  label: string;
}

export interface TokenUsage {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
}

// Posted by a find_content/case_scores widget when a citation link is clicked onsite, carrying the
// same position data ViewerLinkProvider encoded into the link's rectX/rectY/... query params —
// see readWidgetNavigation in mcp-app-host.ts.
export interface CitationNavigation {
  fileReference: string;
  page?: number;
  startTime?: number;
  endTime?: number;
  rectangle?: { x: number; y: number; width: number; height: number; pageWidth: number; pageHeight: number };
}

interface ResponsesOutputItem {
  type: string;
  name?: string;
  arguments?: string;
  output?: string;
  content?: Array<{ type: string; text?: string }>;
  consent_link?: string;
  server_label?: string;
}

interface ResponsesInputItem extends ResponsesOutputItem {
  role?: string;
}

interface ResponsesPayload {
  id: string;
  output: ResponsesOutputItem[];
  status?: string;
  error?: { message?: string };
  metadata?: { unity_session_link_id?: string };
  usage?: { input_tokens: number; output_tokens: number; total_tokens: number };
}

// Shape of a single content block inside a tool's `function_call_output.output` JSON string
// (an MCP CallToolResult), when it carries an embedded UI resource.
interface McpResourceContentBlock {
  type: "resource";
  resource: McpUiResource;
}

// The tool's raw content array (plus structuredContent, needed by widgets like set-case/set-brand
// that render from app.ontoolresult rather than fetching their own data), replayed verbatim to an
// MCP App's AppBridge via sendToolResult (see connectMcpApp in mcp-app-host.ts) so
// app.ontoolresult fires exactly as it would for a host with a live MCP connection driving the
// original tool call.
export type RawCallToolResult = { content: unknown[]; structuredContent?: unknown; isError?: boolean };

// Port of ConversationKeyResolver (mcp-ui-passthrough-agent/Program.cs): the hosted agent stamps
// this same partition as x-ms-client-session-id on every outgoing MCP call for the conversation.
// Computing it here — from the same response id the agent already handed back to us — lets the
// browser's own direct MCP client (mcp-client.ts) address the exact same
// conversation-scoped state on UnityAgent.Proxy/UnityAgent.Api, with no extra round trip.
const NEW_FORMAT_BODY_LENGTH = 50;
const NEW_FORMAT_PARTITION_LENGTH = 18;
const LEGACY_BODY_LENGTH = 48;
const LEGACY_PARTITION_LENGTH = 16;

function partitionOf(id: string | undefined): string | undefined {
  if (!id) return undefined;
  const delimiter = id.indexOf("_");
  const body = delimiter < 0 ? id : id.slice(delimiter + 1);

  if (body.length === NEW_FORMAT_BODY_LENGTH) return body.slice(0, NEW_FORMAT_PARTITION_LENGTH);
  if (body.length === LEGACY_BODY_LENGTH) return body.slice(-LEGACY_PARTITION_LENGTH);
  return id;
}

export interface ChatTurn {
  /** "note" is a divider the host adds, e.g. when the user moves to another case — see noteContextChange. */
  role: "user" | "assistant" | "note";
  events: ChatEvent[];
  usage?: TokenUsage;
  /** The files a user turn carried. */
  files?: FileChip[];
}

export interface AgentReply {
  responseId: string;
  events: ChatEvent[];
  usage?: TokenUsage;
  linkedSessionLinkId?: string;
  error?: string;
}

export function createResponsesClient(agent: AgentConfig, acquireToken: () => Promise<string>) {
  let currentConversationKey: string | undefined;

  /** The conversation key the hosted agent resolved for the current chat, if any turn has completed yet. */
  function getConversationKey(): string | undefined {
    return currentConversationKey;
  }

  function clearConversationKey(): void {
    currentConversationKey = undefined;
  }

  /** Re-points the conversation key at a restored chat, before its MCP App cards mount. */
  function resumeConversationKey(lastResponseId: string): void {
    currentConversationKey = partitionOf(lastResponseId);
  }

  /**
   * Rebuilds a stored conversation from Foundry: input_items gives every item up to the last
   * turn's input (oldest first), and the last response itself gives that turn's output. Items are
   * split into turns at each user message and replayed through extractEvents, so MCP App cards
   * re-render from their stored tool results.
   */
  async function loadConversation(lastResponseId: string): Promise<ChatTurn[]> {
    const accessToken = await acquireToken();
    const get = async <T>(path: string, query: Record<string, string> = {}): Promise<T> => {
      const url = new URL(agent.agentEndpoint);
      url.pathname += `/${encodeURIComponent(lastResponseId)}${path}`;
      for (const [key, value] of Object.entries(query)) url.searchParams.set(key, value);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (!res.ok) throw new Error(`Agent history request failed: ${res.status} ${res.statusText}`);
      return (await res.json()) as T;
    };

    const items: ResponsesInputItem[] = [];
    let after: string | undefined;
    do {
      const page = await get<{ data: ResponsesInputItem[]; has_more: boolean; last_id?: string }>("/input_items", {
        limit: "100",
        order: "asc",
        ...(after ? { after } : {}),
      });
      items.push(...page.data);
      after = page.has_more ? page.last_id : undefined;
    } while (after);
    const last = await get<ResponsesPayload>("");
    items.push(...last.output);

    const turns: ChatTurn[] = [];
    for (const item of items) {
      if (item.type === "message" && item.role === "user") {
        const text = (item.content ?? []).map((block) => block.text ?? "").join("");
        turns.push({ role: "user", events: [{ kind: "text", text }] });
        continue;
      }
      if (turns.at(-1)?.role !== "assistant") turns.push({ role: "assistant", events: [] });
      turns.at(-1)!.events.push(...extractEvents({ id: last.id, output: [item] }));
    }
    return turns;
  }

  async function sendMessage(input: string, previousResponseId?: string, sessionLinkId?: string): Promise<AgentReply> {
    const accessToken = await acquireToken();
    const res = await fetch(agent.agentEndpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
        "x-client-tenant-id": agent.tenantId,
        ...(sessionLinkId ? { "x-client-session-link-id": sessionLinkId } : {}),
        ...(agent.environmentName ? { "x-client-environment-id": agent.environmentName } : {}),
      },
      body: JSON.stringify({
        input,
        stream: false,
        ...(previousResponseId ? { previous_response_id: previousResponseId } : {}),
      }),
    });

    if (!res.ok) {
      throw new Error(`Agent request failed: ${res.status} ${res.statusText}`);
    }

    const payload = (await res.json()) as ResponsesPayload;
    // Same resolution order as ConversationKeyResolver.Resolve: prefer the partition already
    // carried in previousResponseId (stable across the whole chain), falling back to the freshly
    // minted response id on a cold start.
    currentConversationKey = partitionOf(previousResponseId) ?? partitionOf(payload.id);
    const usage = payload.usage
      ? {
          inputTokens: payload.usage.input_tokens,
          outputTokens: payload.usage.output_tokens,
          totalTokens: payload.usage.total_tokens,
        }
      : undefined;

    return {
      responseId: payload.id,
      events: extractEvents(payload),
      usage,
      linkedSessionLinkId: payload.metadata?.unity_session_link_id,
      error:
        payload.status === "failed"
          ? (payload.error?.message ?? "The agent could not complete this request.")
          : undefined,
    };
  }

  return { sendMessage, loadConversation, getConversationKey, clearConversationKey, resumeConversationKey };
}

function extractEvents(payload: ResponsesPayload): ChatEvent[] {
  const events: ChatEvent[] = [];

  for (const item of payload.output) {
    if (item.type === "function_call" && item.name) {
      events.push({ kind: "tool-call", name: item.name, args: item.arguments });
    } else if (item.type === "function_call_output" && item.output) {
      events.push(...extractResourcesFromToolOutput(item.output));
    } else if (item.type === "oauth_consent_request" && item.consent_link) {
      events.push({ kind: "consent", serverLabel: item.server_label ?? "", consentLink: item.consent_link });
    } else if (item.type === "message" && item.content) {
      for (const block of item.content) {
        if (block.type === "output_text" && block.text) {
          events.push({ kind: "text", text: block.text });
        }
      }
    }
  }

  return events;
}

function extractResourcesFromToolOutput(rawOutput: string): ChatEvent[] {
  try {
    const parsed = JSON.parse(rawOutput) as {
      content?: McpResourceContentBlock[];
      structuredContent?: unknown;
      isError?: boolean;
      uiResource?: McpUiResource;
    };
    const content = parsed.content ?? [];
    const toolResult = { content, structuredContent: parsed.structuredContent, isError: parsed.isError };
    if ((parsed.structuredContent as { newChat?: unknown } | undefined)?.newChat === true && !parsed.isError) {
      return [{ kind: "new-chat" }];
    }
    if (parsed.uiResource?.uri != null && parsed.uiResource.uri.length > 0) {
      return [{ kind: "resource", resource: parsed.uiResource, toolResult }];
    }

    return content
      .filter((block): block is McpResourceContentBlock => block.type === "resource")
      .map((block) => ({
        kind: "resource" as const,
        resource: block.resource,
        toolResult,
      }));
  } catch {
    // Tool output wasn't JSON (or didn't carry a resource block) — nothing to render.
    return [];
  }
}
